import { useCallback, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  formatDateFilterValue,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { matchSaleDateRange } from '../../receptionist/sales/saleHistoryFilters';
import { fetchAttendance, fetchAttendancePage } from '../../../services/receptionist/receptionistApi';
import { useServerPagedList } from '../../../hooks/useServerPagedList';
import TablePagination from '../../../components/shared/TablePagination';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import {
  exportAttendanceCsv,
  exportAttendanceExcel,
  exportAttendancePdf,
  exportAttendance,
  printAttendanceList,
} from './exportAttendance';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './attendance.css';

function StatusBadge({ status }) {
  if (status === 'in-gym') {
    return <span className="owner-badge owner-badge-info">In Gym</span>;
  }
  return <span className="owner-badge owner-badge-success">Completed</span>;
}

function formatDuration(checkIn, checkOut) {
  if (!checkIn || !checkOut) return '—';
  return '—';
}

function attendanceStatusLabel(status) {
  return status === 'in-gym' ? 'In Gym' : 'Completed';
}

function mapAttendanceRow(row) {
  return {
    id: row.id,
    member: row.member,
    branch: row.branch || '—',
    checkIn: row.checkIn || '—',
    checkOut: row.checkOut || null,
    date: row.date,
    status: row.status === 'in-gym' ? 'in-gym' : 'completed',
    duration: formatDuration(row.checkIn, row.checkOut),
  };
}

function AttendanceManagement() {
  const [search, setSearch] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    member: '',
    branch: '',
    date: '',
    checkIn: '',
    checkOut: '',
    duration: '',
    status: '',
  });

  const fetchPage = useCallback(async (params) => {
    const result = await fetchAttendancePage(params);
    return {
      ...result,
      rows: (result.rows || []).map(mapAttendanceRow),
    };
  }, []);

  const {
    rows: records,
    total,
    page,
    setPage,
    totalPages,
    from,
    to,
    loading,
    error,
    setError,
    nextPage,
    prevPage,
  } = useServerPagedList({
    fetcher: fetchPage,
    search,
    extra: { dateFrom: rangeStart, dateTo: rangeEnd },
    filters,
  });

  const filteredRecords = records;

  const handleExport = async (exporterFn) => {
    try {
      const all = await fetchAttendance();
      const ranged = all.map(mapAttendanceRow).filter((row) => matchSaleDateRange(row.date, rangeStart, rangeEnd));
      exporterFn(ranged);
    } catch (err) {
      setError(err.message || 'Export failed');
    }
  };

  return (
    <div>
      <PageHeader title="Attendance" subtitle="Track member attendance records" />

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => handleExport(printAttendanceList)}
        onCsv={() => handleExport(exportAttendanceCsv)}
        onPdf={() => handleExport(exportAttendancePdf)}
        onExcel={() => handleExport(exportAttendanceExcel)}
        onExport={() => handleExport(exportAttendance)}
        ariaLabel="Attendance print and export"
      >
        <input
          type="search"
          className="owner-form-input"
          style={{ maxWidth: 260 }}
          placeholder="Search attendance..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search attendance"
        />
      </PrintActionsHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading attendance...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Check In"
                  filterKey="checkIn"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search check in..."
                />
                <ColumnFilterHeader
                  label="Check Out"
                  filterKey="checkOut"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search check out..."
                />
                <ColumnFilterHeader
                  label="Duration"
                  filterKey="duration"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search duration..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{record.member}</td>
                    <td>{record.branch}</td>
                    <td>{record.date}</td>
                    <td>{record.checkIn}</td>
                    <td>{record.checkOut || '—'}</td>
                    <td>{record.duration}</td>
                    <td><StatusBadge status={record.status} /></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No attendance records found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>
    </div>
  );
}

export default AttendanceManagement;
